import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import type { APO6OptionLetter } from '@/config/instruments';
import { APO_6_ITEMS } from '@/config/instruments';

export interface APO6RunnerProps {
  commercialName: string;
  onComplete: (choices: APO6OptionLetter[]) => void;
  initialChoices?: APO6OptionLetter[];
}

const INSTRUCTION =
  'Instrucciones: Responde con calma. En estas preguntas importa la precisión.';

export function APO6Runner({
  commercialName,
  onComplete,
  initialChoices = [],
}: APO6RunnerProps) {
  const pad6 = (arr: APO6OptionLetter[]) =>
    [...arr, ...(Array(6 - arr.length).fill(undefined) as (APO6OptionLetter | undefined)[])].slice(0, 6);
  const [choices, setChoices] = useState<(APO6OptionLetter | undefined)[]>(() => pad6(initialChoices));
  const [currentIndex, setCurrentIndex] = useState(0);

  const setChoice = useCallback((idx: number, letter: APO6OptionLetter) => {
    setChoices((prev) => {
      const next = [...prev];
      next[idx] = letter;
      return next;
    });
  }, []);

  const item = APO_6_ITEMS[currentIndex];
  const choice = choices[currentIndex];

  const allComplete = APO_6_ITEMS.every((_, i) => choices[i] != null);

  const handleNext = () => {
    if (currentIndex < APO_6_ITEMS.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (allComplete) {
      onComplete(choices.filter((x): x is APO6OptionLetter => x != null) as APO6OptionLetter[]);
    }
  };

  const handleSubmit = () => {
    if (!allComplete) return;
    onComplete(choices.filter((x): x is APO6OptionLetter => x != null) as APO6OptionLetter[]);
  };

  if (!item) return null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-lg">{commercialName}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Pregunta {currentIndex + 1} de {APO_6_ITEMS.length}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {currentIndex === 0 && (
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
            {INSTRUCTION}
          </p>
        )}
        <p className="font-medium whitespace-pre-line">{item.stem}</p>
        <div className="flex flex-col gap-2">
          {item.options.map((opt) => (
            <label
              key={opt.letter}
              className="flex cursor-pointer items-center gap-2 rounded-md border p-3 hover:bg-muted/50"
            >
              <input
                type="radio"
                name={item.id}
                checked={choice === opt.letter}
                onChange={() => setChoice(currentIndex, opt.letter)}
              />
              <span className="text-sm">
                <strong>{opt.letter})</strong> {opt.text}
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            disabled={currentIndex === 0}
            onClick={() => setCurrentIndex((i) => i - 1)}
          >
            Anterior
          </Button>
          {currentIndex < APO_6_ITEMS.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!choice}
            >
              Siguiente
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={!allComplete}>
              Finalizar prueba
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Respondidas: {choices.filter((c) => c != null).length} / {APO_6_ITEMS.length}
        </p>
      </CardContent>
    </Card>
  );
}
